import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    List, 
    ListItem, 
    Paper, 
    CircularProgress, 
    useTheme, 
    Fade 
} from '@mui/material';
import { searchCodes, getCode, logActivity } from '../services/api';
import { saveToHistory } from '../services/historyService';
import CodeHistory from './CodeHistory';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

const UserConsole = () => {
    const [code, setCode] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
    const [isHistoryEmpty, setIsHistoryEmpty] = useState(true);
    const detailRef = useRef(null);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const historyHeaderRef = useRef(null);
    const titleRef = useRef(null);
    const hasScrolledRef = useRef(false);

    const scrollToTitle = useCallback(() => {
        if (typeof window === 'undefined' || window.innerWidth >= 900) return;
        if (titleRef.current) {
            try {
                titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (e) {
                const headerOffset = 72; // 60px fixed header + 12px breathing room
                const elementPosition = titleRef.current.getBoundingClientRect().top;
                const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                const targetScroll = currentScroll + elementPosition - headerOffset;
                window.scrollTo({
                    top: Math.max(0, targetScroll),
                    behavior: 'smooth'
                });
            }
        }
    }, []);

    useEffect(() => {
        const history = JSON.parse(localStorage.getItem('codeHistory') || '[]');
        setIsHistoryEmpty(history.length === 0);
        setIsHistoryExpanded(history.length > 0);
    }, []);

    useEffect(() => {
        const handleHistoryUpdate = () => {
            const history = JSON.parse(localStorage.getItem('codeHistory') || '[]');
            setIsHistoryEmpty(history.length === 0);
        };

        window.addEventListener('historyUpdated', handleHistoryUpdate);
        return () => {
            window.removeEventListener('historyUpdated', handleHistoryUpdate);
        };
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            const isHistoryHeaderClick = historyHeaderRef.current && 
                (historyHeaderRef.current.contains(event.target) ||
                 event.target.closest('[data-history-toggle]'));

            if (detailRef.current && 
                !detailRef.current.contains(event.target) && 
                !isHistoryHeaderClick) {
                if (selectedCode || error) {
                    handleDetailClose();
                }
            }
        }

        if (selectedCode || error) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [selectedCode, error]);

    const updateHistory = useCallback((code) => {
        if (!code) return;
        saveToHistory(code);
    }, []);

    const handleDetailClose = useCallback(() => {
        if (selectedCode) {
            updateHistory(selectedCode);
        }
        setIsDetailVisible(false);
        setSelectedCode(null);
        setError(null);
        setIsLoading(false);
    }, [selectedCode, updateHistory]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (selectedCode) {
                updateHistory(selectedCode);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [selectedCode, updateHistory]);

    const handleCodeChange = async (e) => {
        const value = e.target.value.toUpperCase();
        if (value.length <= 20) {
            setCode(value);

            if (isDetailVisible) {
                handleDetailClose();
            }

            if (value.length >= 1) {
                // Scroll screen up on mobile so suggestions have maximum visible space
                scrollToTitle();
                try {
                    const results = await searchCodes(value);
                    const sortedResults = results.sort((a, b) => a.code.localeCompare(b.code));
                    setSuggestions(sortedResults);
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setSuggestions([]);
                }
            } else {
                setSuggestions([]);
                hasScrolledRef.current = false;
            }
        }
    };

    const handleInputFocus = () => {
        setIsInputFocused(true);
        // On mobile, scroll up as keyboard opens
        if (typeof window !== 'undefined' && window.innerWidth < 900) {
            setTimeout(scrollToTitle, 100);
            setTimeout(scrollToTitle, 300);
        }
        if (code.length >= 1 && suggestions.length === 0) {
            handleCodeChange({ target: { value: code } });
        }
    };

    const handleInputBlur = () => {
        setTimeout(() => {
            setIsInputFocused(false);
            hasScrolledRef.current = false;
        }, 250);
    };

    const handleCodeSelect = async (codeValue, shouldShowInInput = false, suggestionData = null) => {
        try {
            setCode(codeValue.toUpperCase());
            setSelectedCode(null);
            setError(null);
            setIsLoading(true);
            setIsInputFocused(false);
            setSuggestions([]);

            const codeData = await getCode(codeValue);
            
            if (suggestionData && suggestionData.imageUrl) {
                codeData.imageUrl = suggestionData.imageUrl;
            }
            
            setSelectedCode(codeData);
            setIsLoading(false);

            try {
                await logActivity('visit_code', codeData.code, codeData.code_id, codeData.owner_id);
            } catch (error) {
                console.error('Error logging activity:', error);
            }

            const link = document.createElement('a');
            link.href = codeData.url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            updateHistory(codeData);

            setTimeout(() => {
                setCode('');
                setSelectedCode(null);
            }, 2500);

        } catch (error) {
            console.error('Error fetching code:', error);
            setSelectedCode(null);
            setError(`Code "${codeValue}" was not found. You can create it as a Code Owner.`);
            setIsLoading(false);
            setCode('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (code) {
            const enteredCode = code.trim();
            handleCodeSelect(enteredCode);
        }
    };

    const handleHistoryItemClick = useCallback((historyCode) => {
        if (selectedCode || error) {
            handleDetailClose();
        }
    }, [selectedCode, error, handleDetailClose]);

    useEffect(() => {
        const logViewActivity = async () => {
            if (selectedCode) {
                try {
                    await logActivity('view_code', selectedCode.code, selectedCode.code_id);
                } catch (error) {
                    console.error('Error logging activity:', error);
                }
            }
        };

        logViewActivity();
    }, [selectedCode]);

    return (
        <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1, sm: 2 },
            py: 1,
            pb: isInputFocused ? { xs: '380px', sm: '200px', md: 1 } : 1,
            transition: 'padding-bottom 0.25s ease'
        }}>
            {/* Header / Intro */}
            <Box sx={{ 
                flex: 'none',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
            }}> 
                <Typography 
                    ref={titleRef}
                    variant="h4"
                    sx={{ 
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.25rem', sm: '1.8rem', md: '2.0rem' },
                        mb: { xs: 2, sm: 2.5 },
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        scrollMarginTop: { xs: '72px', sm: '80px' }
                    }}
                >
                    Seen or Heard a Code? Check it out!
                </Typography>

                {/* Unified Modern Search Form */}
                <Box 
                    component="form" 
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: '520px',
                        mb: 2,
                        position: 'relative',
                        backgroundColor: isDark ? '#141E30' : '#FFFFFF',
                        borderRadius: '16px',
                        border: `1.5px solid ${isInputFocused ? theme.palette.primary.main : theme.palette.divider}`,
                        boxShadow: isInputFocused 
                            ? (isDark ? '0 0 0 4px rgba(0, 102, 255, 0.25)' : '0 0 0 4px rgba(0, 102, 255, 0.15)')
                            : (isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(15, 23, 42, 0.04)'),
                        p: { xs: '8px 14px', sm: '10px 18px' },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <SearchRoundedIcon 
                        sx={{ 
                            color: isInputFocused ? theme.palette.primary.main : theme.palette.text.secondary, 
                            mr: 1.25,
                            fontSize: '1.45rem',
                            transition: 'color 0.2s ease',
                            opacity: 0.85
                        }} 
                    />
                    <TextField
                        value={code}
                        onChange={handleCodeChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder="Enter code (e.g. SG2025, NY3344)"
                        fullWidth
                        autoComplete="off"
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            style: {
                                fontSize: '1.05rem',
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                color: theme.palette.text.primary,
                            }
                        }}
                        inputProps={{
                            autoCorrect: 'off',
                            autoCapitalize: 'characters',
                            spellCheck: 'false',
                        }}
                        sx={{
                            flex: 1,
                        }}
                    />

                    {/* Suggestions dropdown */}
                    {isInputFocused && suggestions.length > 0 && (
                        <Paper
                            sx={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                left: 0,
                                right: 0,
                                maxHeight: { xs: '290px', sm: '350px' },
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                backgroundColor: isDark ? '#141E30' : '#FFFFFF',
                                borderRadius: '16px',
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: isDark 
                                    ? '0 20px 40px rgba(0, 0, 0, 0.7)' 
                                    : '0 20px 40px -10px rgba(15, 23, 42, 0.15)',
                                zIndex: 1200,
                                p: { xs: 0.75, sm: 1 },
                                '&::-webkit-scrollbar': {
                                    width: '6px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: theme.palette.divider,
                                    borderRadius: '3px',
                                },
                            }}
                        >
                            <List sx={{ p: 0 }}>
                                {suggestions.map((suggestion) => (
                                    <ListItem
                                        key={suggestion.code}
                                        component="a"
                                        href={suggestion.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={async (e) => {
                                            try {
                                                await logActivity(
                                                    'visit_code', 
                                                    suggestion.code, 
                                                    suggestion.code_id, 
                                                    suggestion.owner_id
                                                );
                                                updateHistory(suggestion);
                                                setCode('');
                                                setSuggestions([]);
                                                window.dispatchEvent(new Event('historyUpdated'));
                                            } catch (error) {
                                                console.error('Error logging activity:', error);
                                            }
                                        }}
                                        sx={{
                                            p: { xs: 1, sm: 1.25 },
                                            borderRadius: '12px',
                                            mb: 0.5,
                                            transition: 'all 0.15s ease',
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            '&:hover': {
                                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 102, 255, 0.06)',
                                                '& .suggestion-arrow': {
                                                    color: theme.palette.primary.main,
                                                    transform: 'translateX(3px)',
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: { xs: 1.25, sm: 1.5 }, 
                                            width: '100%', 
                                            minWidth: 0 
                                        }}>
                                            {/* Thumbnail */}
                                            <Box
                                                sx={{
                                                    width: { xs: 40, sm: 44 },
                                                    height: { xs: 40, sm: 44 },
                                                    flexShrink: 0,
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {suggestion.imageUrl ? (
                                                    <img
                                                        src={suggestion.imageUrl}
                                                        alt={suggestion.code}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <Box sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: '#FFFFFF',
                                                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                                        fontWeight: 700
                                                    }}>
                                                        {suggestion.code.substring(0, 2)}
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Details */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
                                                    <Typography 
                                                        variant="body1"
                                                        sx={{ 
                                                            color: theme.palette.primary.main,
                                                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                                            fontWeight: 800,
                                                            letterSpacing: '0.02em',
                                                            flexShrink: 0,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {suggestion.code}
                                                    </Typography>
                                                    {suggestion.codeName && (
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                color: theme.palette.text.primary,
                                                                fontWeight: 600,
                                                                fontSize: { xs: '0.82rem', sm: '0.88rem' },
                                                                flex: 1,
                                                                minWidth: 0,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            • {suggestion.codeName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography 
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ 
                                                        color: theme.palette.text.secondary,
                                                        fontSize: { xs: '0.78rem', sm: '0.82rem' },
                                                        lineHeight: 1.25,
                                                        mt: 0.25
                                                    }}
                                                >
                                                    {suggestion.codeDescription || suggestion.url}
                                                </Typography>
                                            </Box>

                                            <ArrowForwardRoundedIcon 
                                                className="suggestion-arrow" 
                                                sx={{ 
                                                    fontSize: 16, 
                                                    color: theme.palette.text.secondary,
                                                    transition: 'all 0.2s ease',
                                                    flexShrink: 0
                                                }} 
                                            />
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>

                {/* Loading indicator, Error notification, or Selected Card */}
                {(isLoading || error || selectedCode) && (
                    <Box ref={detailRef} sx={{ width: '100%', maxWidth: '520px', mb: 2 }}>
                        {isLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                                <CircularProgress size={22} thickness={4} sx={{ color: theme.palette.primary.main }} />
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                    Finding verified destination...
                                </Typography>
                            </Box>
                        )}

                        {error && !isLoading && (
                            <Fade in={Boolean(error)}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.25,
                                    p: 1.75,
                                    borderRadius: '12px',
                                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
                                    border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
                                    color: theme.palette.error.main,
                                }}>
                                    <ErrorOutlineRoundedIcon sx={{ fontSize: 20, mt: '2px', flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {error}
                                    </Typography>
                                </Box>
                            </Fade>
                        )}

                        {selectedCode && !isLoading && (
                            <Paper
                                component="a"
                                href={selectedCode.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={async (e) => {
                                    try {
                                        await logActivity('visit_code', selectedCode.code, selectedCode.code_id, selectedCode.owner_id);
                                    } catch (err) {
                                        console.error('Error logging activity:', err);
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: '16px',
                                    backgroundColor: isDark ? '#141E30' : '#FFFFFF',
                                    border: `1px solid ${isDark ? 'rgba(0, 102, 255, 0.3)' : 'rgba(0, 102, 255, 0.25)'}`,
                                    boxShadow: isDark 
                                        ? '0 10px 30px rgba(0, 0, 0, 0.4)' 
                                        : '0 8px 24px -6px rgba(0, 102, 255, 0.12)',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: isDark 
                                            ? '0 16px 36px rgba(0, 0, 0, 0.5)' 
                                            : '0 12px 30px -4px rgba(0, 102, 255, 0.2)',
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        flexShrink: 0,
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
                                        border: `1px solid ${theme.palette.divider}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {selectedCode.imageUrl ? (
                                        <img
                                            src={selectedCode.imageUrl}
                                            alt={selectedCode.code}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: theme.palette.primary.main,
                                            color: '#FFFFFF',
                                            fontSize: '1.1rem',
                                            fontWeight: 700
                                        }}>
                                            {selectedCode.code.substring(0, 2)}
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography 
                                            variant="subtitle1" 
                                            sx={{ 
                                                color: theme.palette.primary.main, 
                                                fontWeight: 800,
                                                fontSize: '1.05rem',
                                                letterSpacing: '0.02em'
                                            }}
                                        >
                                            {selectedCode.code}
                                        </Typography>
                                        {selectedCode.codeName && (
                                            <Typography 
                                                variant="body2" 
                                                noWrap 
                                                sx={{ 
                                                    color: theme.palette.text.primary, 
                                                    fontWeight: 600 
                                                }}
                                            >
                                                • {selectedCode.codeName}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography 
                                        variant="body2" 
                                        noWrap 
                                        sx={{ 
                                            color: theme.palette.text.secondary, 
                                            fontSize: '0.85rem' 
                                        }}
                                    >
                                        {selectedCode.codeDescription || selectedCode.url}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    backgroundColor: theme.palette.primary.main,
                                    color: '#FFFFFF',
                                    flexShrink: 0
                                }}>
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                                </Box>
                            </Paper>
                        )}
                    </Box>
                )}
            </Box>

            {/* Scrollable history section */}
            <Box sx={{ 
                overflow: 'auto',
                mt: 1.5,
                width: '100%',
                maxWidth: '520px',
            }}>
                <Box
                    ref={historyHeaderRef}
                    onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                    data-history-toggle="true"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        py: 1,
                        px: 0.5,
                        borderRadius: '10px',
                        userSelect: 'none',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.03)',
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryRoundedIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography 
                            variant="subtitle1"
                            sx={{ 
                                color: theme.palette.text.primary,
                                fontWeight: 700,
                                fontSize: { xs: '0.95rem', sm: '1.1rem' }
                            }}
                        >
                            My Codes History
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                            variant="caption"
                            sx={{ 
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                            }}
                        >
                            {isHistoryExpanded ? 'Collapse' : 'Expand'}
                        </Typography>
                        {isHistoryExpanded ? 
                            <KeyboardArrowUpRoundedIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} /> : 
                            <KeyboardArrowDownRoundedIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                        }
                    </Box>
                </Box>

                {isHistoryExpanded && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                        <CodeHistory onHistoryItemClick={handleHistoryItemClick} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default UserConsole;